import type { EmbedMode } from './UseHestiaIframe'

export const HESTIA_TO_PARENT_RELAY_REQUEST = 'HESTIA_TO_PARENT_RELAY_REQUEST'
export const HESTIA_INBOX_ROUTE_SYNC = 'HESTIA_INBOX_ROUTE_SYNC'
export const HESTIA_INBOX_UNREAD_SYNC = 'HESTIA_INBOX_UNREAD_SYNC'
export const HESTIA_PHONE_ORDERING_ROUTE_SYNC =
  'HESTIA_PHONE_ORDERING_ROUTE_SYNC'
export const HESTIA_PRINT_SHOP_ROUTE_SYNC = 'HESTIA_PRINT_SHOP_ROUTE_SYNC'
export const HESTIA_ORDERS_ROUTE_SYNC = 'HESTIA_ORDERS_ROUTE_SYNC'
export const HESTIA_UPSELL_OPEN_REQUEST = 'HESTIA_UPSELL_OPEN_REQUEST'
export const PARENT_TO_HESTIA_EVENT = 'PARENT_TO_HESTIA_EVENT'
export const HESTIA_WEBSITE_BUILDER_LAYOUT_READY =
  'HESTIA_WEBSITE_BUILDER_LAYOUT_READY'
// Embedded Hestia asks Hermes to run the GBP Google OAuth at top level — Google
// rejects OAuth driven from inside the iframe. MUST stay in sync with the mirror
// constant in apps/hestia/src/utilities/iframe/gbpOAuthRelay.ts — together they
// form the postMessage protocol across the app boundary (no shared import).
// Change both or this receiver silently drops every message.
export const HESTIA_GBP_OAUTH_REQUEST = 'HESTIA_GBP_OAUTH_REQUEST'
// Embedded Hestia asks Hermes to raise/lower a full-window scrim while a
// blocking modal is open inside the iframe, so the Hermes left-nav dims along
// with the embed content (the in-iframe overlay can't reach outside the embed
// box). MUST stay in sync with the mirror constant in
// apps/hestia/src/utilities/iframe/modalScrimRelay.ts — together they form the
// postMessage protocol across the app boundary (no shared import). Change both
// or this receiver silently drops every message.
export const HESTIA_MODAL_SCRIM_SYNC = 'HESTIA_MODAL_SCRIM_SYNC'

export enum HestiaIframeTarget {
  OwnerAgentWidget = 'owner-agent-widget',
  WebsiteBuilder = 'website-builder-hermes',
}

export enum HestiaIframeEvent {
  WebsitePreviewReload = 'website-preview.reload',
  OwnerAgentOpened = 'owner-agent.opened',
  OwnerAgentClosed = 'owner-agent.closed',
}

export const HESTIA_ORDERS_ROUTE_QUERY_KEYS = [
  'end',
  'menuType',
  'orderId',
  'orderLocationId',
  'page',
  'query',
  'start',
  'status',
  'tab',
  'taxExemptionEnabled',
  'type',
] as const

export type HestiaOrdersRouteQueryKey =
  (typeof HESTIA_ORDERS_ROUTE_QUERY_KEYS)[number]

export type HestiaOrdersRouteQuery = Record<
  HestiaOrdersRouteQueryKey,
  string | undefined
>

export interface HestiaToParentRelayRequest {
  event: HestiaIframeEvent
  target: HestiaIframeTarget
  type: typeof HESTIA_TO_PARENT_RELAY_REQUEST
}

export interface HestiaWebsiteBuilderLayoutReadyMessage {
  type: typeof HESTIA_WEBSITE_BUILDER_LAYOUT_READY
}

export interface HestiaPhoneOrderingRouteSyncMessage {
  callId: string | null
  type: typeof HESTIA_PHONE_ORDERING_ROUTE_SYNC
}

export interface HestiaPrintShopRouteSyncMessage {
  orderId: string | null
  type: typeof HESTIA_PRINT_SHOP_ROUTE_SYNC
}

export interface HestiaOrdersRouteSyncMessage {
  // The order's own location — carried alongside orderId so a deep link can
  // reopen a brand-scoped order that lives at a different location than the
  // dashboard's active one (getOrder is location-scoped).
  locationId: string | null
  orderId: string | null
  query?: Partial<HestiaOrdersRouteQuery>
  type: typeof HESTIA_ORDERS_ROUTE_SYNC
}

export interface HestiaInboxRouteSyncMessage {
  threadId: string | null
  type: typeof HESTIA_INBOX_ROUTE_SYNC
}

export interface HestiaInboxUnreadSyncMessage {
  type: typeof HESTIA_INBOX_UNREAD_SYNC
}

export interface ParentToHestiaEvent {
  event: HestiaIframeEvent
  source?: HestiaIframeTarget
  type: typeof PARENT_TO_HESTIA_EVENT
}

export interface HestiaGbpOAuthRequestMessage {
  brandId: string
  locationId: string
  type: typeof HESTIA_GBP_OAUTH_REQUEST
}

export interface HestiaModalScrimSyncMessage {
  isOpen: boolean
  type: typeof HESTIA_MODAL_SCRIM_SYNC
}

export interface HestiaUpsellOpenRequestMessage {
  source: string
  type: typeof HESTIA_UPSELL_OPEN_REQUEST
}

interface BuildParentToHestiaEventParams {
  event: HestiaIframeEvent
  source?: HestiaIframeTarget
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object'
}

const isHestiaOrdersRouteQuery = (
  value: unknown
): value is Partial<HestiaOrdersRouteQuery> => {
  if (!isRecord(value)) return false

  return Object.entries(value).every(([key, queryValue]) => {
    return (
      HESTIA_ORDERS_ROUTE_QUERY_KEYS.includes(
        key as HestiaOrdersRouteQueryKey
      ) &&
      (typeof queryValue === 'string' || queryValue === undefined)
    )
  })
}

export const isHestiaGbpOAuthRequest = (
  data: unknown
): data is HestiaGbpOAuthRequestMessage => {
  return (
    isRecord(data) &&
    data.type === HESTIA_GBP_OAUTH_REQUEST &&
    typeof data.brandId === 'string' &&
    typeof data.locationId === 'string'
  )
}

export const isHestiaModalScrimSyncMessage = (
  value: unknown
): value is HestiaModalScrimSyncMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_MODAL_SCRIM_SYNC &&
    typeof value.isOpen === 'boolean'
  )
}

export const isHestiaUpsellOpenRequestMessage = (
  value: unknown
): value is HestiaUpsellOpenRequestMessage => {
  return (
    isRecord(value) &&
    value.type === HESTIA_UPSELL_OPEN_REQUEST &&
    typeof value.source === 'string'
  )
}

export const isHestiaIframeTarget = (
  value: unknown
): value is HestiaIframeTarget => {
  return Object.values(HestiaIframeTarget).includes(value as HestiaIframeTarget)
}

export const isHestiaIframeEvent = (
  value: unknown
): value is HestiaIframeEvent => {
  return Object.values(HestiaIframeEvent).includes(value as HestiaIframeEvent)
}

export const normalizeEmbedToHestiaIframeTarget = (
  embed: EmbedMode | null | undefined
): HestiaIframeTarget | null => {
  switch (embed) {
    case 'owner-agent-widget':
      return HestiaIframeTarget.OwnerAgentWidget
    case 'website-builder-hermes':
      return HestiaIframeTarget.WebsiteBuilder
    default:
      return null
  }
}

export const buildParentToHestiaEvent = ({
  event,
  source,
}: BuildParentToHestiaEventParams): ParentToHestiaEvent => {
  return {
    event,
    type: PARENT_TO_HESTIA_EVENT,
    ...(source !== undefined ? { source } : {}),
  }
}

export const isHestiaToParentRelayRequest = (
  value: unknown
): value is HestiaToParentRelayRequest => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_TO_PARENT_RELAY_REQUEST &&
    isHestiaIframeTarget(value.target) &&
    isHestiaIframeEvent(value.event)
  )
}

export const isHestiaWebsiteBuilderLayoutReadyMessage = (
  value: unknown
): value is HestiaWebsiteBuilderLayoutReadyMessage => {
  return isRecord(value) && value.type === HESTIA_WEBSITE_BUILDER_LAYOUT_READY
}

export const isHestiaPhoneOrderingRouteSyncMessage = (
  value: unknown
): value is HestiaPhoneOrderingRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_PHONE_ORDERING_ROUTE_SYNC &&
    (typeof value.callId === 'string' || value.callId === null)
  )
}

export const isHestiaPrintShopRouteSyncMessage = (
  value: unknown
): value is HestiaPrintShopRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_PRINT_SHOP_ROUTE_SYNC &&
    (typeof value.orderId === 'string' || value.orderId === null)
  )
}

export const isHestiaOrdersRouteSyncMessage = (
  value: unknown
): value is HestiaOrdersRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_ORDERS_ROUTE_SYNC &&
    (typeof value.orderId === 'string' || value.orderId === null) &&
    (typeof value.locationId === 'string' || value.locationId === null) &&
    (value.query === undefined || isHestiaOrdersRouteQuery(value.query))
  )
}

export const isHestiaInboxRouteSyncMessage = (
  value: unknown
): value is HestiaInboxRouteSyncMessage => {
  if (!isRecord(value)) return false

  return (
    value.type === HESTIA_INBOX_ROUTE_SYNC &&
    (typeof value.threadId === 'string' || value.threadId === null)
  )
}

export const isHestiaInboxUnreadSyncMessage = (
  value: unknown
): value is HestiaInboxUnreadSyncMessage => {
  if (!isRecord(value)) return false

  return value.type === HESTIA_INBOX_UNREAD_SYNC
}
